import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('hexopdf_token');
  const getRefreshToken = () => localStorage.getItem('hexopdf_refresh');

  const setTokens = (access, refresh) => {
    localStorage.setItem('hexopdf_token', access);
    if (refresh) localStorage.setItem('hexopdf_refresh', refresh);
  };

  const clearTokens = () => {
    localStorage.removeItem('hexopdf_token');
    localStorage.removeItem('hexopdf_refresh');
  };

  const fetchUser = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          const refresh = getRefreshToken();
          if (refresh) {
            const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
            setTokens(res.data.access_token, res.data.refresh_token);
            const userRes = await axios.get(`${API}/me`, {
              headers: { Authorization: `Bearer ${res.data.access_token}` }
            });
            setUser(userRes.data);
          } else {
            clearTokens();
          }
        } catch {
          clearTokens();
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setTokens(res.data.access_token, res.data.refresh_token);
    await fetchUser();
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    setTokens(res.data.access_token, res.data.refresh_token);
    await fetchUser();
    return res.data;
  };

  const loginWithGoogle = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
    setTokens(res.data.access_token, null);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/google/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, getToken, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
